import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Plus, Edit2, Trash2, Award, DollarSign, Dumbbell, FileText, Layers, Users } from "lucide-react";
import AddDialog from "../../components/Modal/AddModal";
import TextInput from "../../components/Select/InputText";
import { useAuth } from "../../context/AuthContext";
import { addCoachTypeService, getCoachTypeService, updateCoachTypeService } from "../../services/coachTypeService";
import { addBranchService, getBranchService, updateBranchService } from "../../services/branchService";
import { addIncomeTypeService, getIncomeTypeService, updateIncomeTypeService } from "../../services/incomeService";
import { addExpenseTypeService, getExpensesTypeService, updateExpenseTypeService } from "../../services/expensesService";
import {
    addMemberTypeService,
    deleteMemberTypeService,
    getMemberTypeService,
    updateMemberTypeService,
} from "../../services/memberTypeService";
import { showErrorToast } from "../../services/toastService";
import { hasPermission } from "../../utils/permissions";
import { FEATURE_KEYS, hasFeature } from "../../utils/featureAccess";
import { addActivityType, deleteActivityType, getActivityTypes, updateActivityType } from "../../services/activityTypeService";
// Import your service functions here
// import { addCoachTypeService, getCoachTypes, updateCoachTypeService, deleteCoachTypeService } from "../../services/coachService";

const Settings = () => {
    const { user } = useAuth();
    const canEditSettings = hasPermission(user, "settings.write");
    const canUseMemberTypes = hasFeature(FEATURE_KEYS.MEMBERS_SUBSCRIPTIONS);
    const canUseCoachTypes = hasFeature(FEATURE_KEYS.PRIVATE_TRAINING);
    const canUseActivityTypes = hasFeature(FEATURE_KEYS.MEMBERS_SUBSCRIPTIONS);
    const canUseBranches = true;
    const canUseIncomeTypes = hasFeature(FEATURE_KEYS.ACCOUNTING);
    const canUseExpenseTypes = hasFeature(FEATURE_KEYS.ACCOUNTING);
    const [activeTab, setActiveTab] = useState("coach");

    const [coachTypes, setCoachTypes] = useState([]);
    const [activityTypes, setActivityTypes] = useState([]);
    const [branchTypes, setBranchTypes] = useState([]);
    const [memberTypes, setMemberTypes] = useState([]);

    const [incomeType, setIncomeTypes] = useState([]);
    const [expensesType, setExpensesTypes] = useState([]);

    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState("");
    const [editingItem, setEditingItem] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const readArray = (value) => {
        if (Array.isArray(value)) return value;
        if (value && typeof value === "object") {
            if (Array.isArray(value.data)) return value.data;
            if (Array.isArray(value.items)) return value.items;
            if (Array.isArray(value.types)) return value.types;
        }
        return [];
    };

    const getItemId = (item) => item?._id ?? item?.id;

    const availableTabs = [
        canUseCoachTypes ? "coach" : null,
        canUseActivityTypes ? "activity" : null,
        canUseMemberTypes ? "member" : null,
        canUseBranches ? "branches" : null,
        canUseIncomeTypes ? "income" : null,
        canUseExpenseTypes ? "expenses" : null,
    ].filter(Boolean);

    useEffect(() => {
        if (!availableTabs.length) return;
        if (!availableTabs.includes(activeTab)) setActiveTab(availableTabs[0]);
    }, [activeTab, availableTabs]);

    // Fetch data on component mount
    useEffect(() => {
        const fetchData = async () => {
            if (!user?.id) return;
            setLoading(true);
            setError("");

            const requests = await Promise.allSettled([
                canUseCoachTypes ? getCoachTypeService() : Promise.resolve([]),
                canUseMemberTypes ? getMemberTypeService() : Promise.resolve([]),
                canUseBranches ? getBranchService() : Promise.resolve([]),
                canUseIncomeTypes ? getIncomeTypeService() : Promise.resolve([]),
                canUseActivityTypes ? getActivityTypes(true) : Promise.resolve([]),
                canUseExpenseTypes ? getExpensesTypeService() : Promise.resolve([]),
            ]);

            const [coaches, members, branch, income, activities, expenses] = requests;

            if (coaches.status === "fulfilled") {
                setCoachTypes(readArray(coaches.value));
            } else {
                console.error("Failed to fetch coach types:", coaches.reason);
            }

            if (members.status === "fulfilled") {
                setMemberTypes(readArray(members.value));
            } else {
                console.error("Failed to fetch member types:", members.reason);
            }

            if (branch.status === "fulfilled") {
                setBranchTypes(readArray(branch.value));
            } else {
                console.error("Failed to fetch branches:", branch.reason);
            }

            if (income.status === "fulfilled") {
                setIncomeTypes(readArray(income.value));
            } else {
                console.error("Failed to fetch income types:", income.reason);
            }

            if (activities.status === "fulfilled") {
                setActivityTypes(readArray(activities.value));
            } else {
                console.error("Failed to fetch activity types:", activities.reason);
            }

            if (expenses.status === "fulfilled") {
                setExpensesTypes(readArray(expenses.value));
            } else {
                console.error("Failed to fetch expense types:", expenses.reason);
            }

            const failedRequiredSettings = [
                canUseCoachTypes ? coaches : null,
                canUseBranches ? branch : null,
                canUseActivityTypes ? activities : null,
            ].filter(Boolean).some((request) => request.status === "rejected");
            if (failedRequiredSettings) {
                setError("Some settings data could not be loaded");
            }

            setLoading(false);
        };

        fetchData();
    }, [user, canUseCoachTypes, canUseActivityTypes, canUseMemberTypes, canUseBranches, canUseIncomeTypes, canUseExpenseTypes]);

    // Formik for form handling
    const formik = useFormik({
        initialValues: {
            name: "",
        },
        validationSchema: Yup.object({
            name: Yup.string().required("Name is required"),
        }),
        onSubmit: async (values) => {
            try {
                if (!user) {
                    showErrorToast("User not logged in");
                    return;
                }

                const payload = { ...values };

                if (editingItem) {
                    // Update existing item
                    if (modalType === "coach") {
                        const updatedCoach = await updateCoachTypeService(editingItem._id, payload);

                        setCoachTypes(prev =>
                            prev.map(coach =>
                                getItemId(coach) === getItemId(updatedCoach) ? updatedCoach : coach
                            )
                        );
                    } else if (modalType === "activity") {
                        const updatedActivity = await updateActivityType(editingItem._id ?? editingItem.id, payload);
                        setActivityTypes(prev => prev.map(item =>
                            getItemId(item) === getItemId(updatedActivity) ? updatedActivity : item
                        ));
                    } else if (modalType === "member") {
                        const updatedMemberType = await updateMemberTypeService(editingItem._id ?? editingItem.id, payload);
                        setMemberTypes(prev =>
                            prev.map(item =>
                                getItemId(item) === getItemId(updatedMemberType) ? updatedMemberType : item
                            )
                        );
                    } else if (modalType === "branch") {
                        const updatedBranch = await updateBranchService(editingItem._id, payload);
                        setBranchTypes(prev =>
                            prev.map(branch => getItemId(branch) === getItemId(updatedBranch) ? { ...branch, ...values } : branch)
                        );
                    } else if (modalType === "income") {
                        const updatedIncomeType = await updateIncomeTypeService(editingItem._id ?? editingItem.id, payload);
                        setIncomeTypes(prev => prev.map(item =>
                            getItemId(item) === getItemId(updatedIncomeType) ? updatedIncomeType : item
                        ));
                    } else if (modalType === "expenses") {
                        const updatedExpenseType = await updateExpenseTypeService(editingItem._id ?? editingItem.id, payload);
                        setExpensesTypes(prev => prev.map(item =>
                            getItemId(item) === getItemId(updatedExpenseType) ? updatedExpenseType : item
                        ));
                    }

                } else {
                    // Add new item
                    if (modalType === "coach") {
                        const savedCoach = await addCoachTypeService(payload);
                        setCoachTypes(prev => [savedCoach, ...prev]);
                    } else if (modalType === "activity") {
                        const savedActivity = await addActivityType(payload);
                        setActivityTypes(prev => [savedActivity, ...prev]);
                    } else if (modalType === "member") {
                        const savedMemberType = await addMemberTypeService(payload);
                        setMemberTypes(prev => [savedMemberType, ...prev]);
                    } else if (modalType === "branch") {
                        const savedBranch = await addBranchService(payload);
                        setBranchTypes(prev => [savedBranch, ...prev]);
                    } else if (modalType === 'income') {
                        const savedIncome = await addIncomeTypeService(payload);
                        setIncomeTypes(prev => [savedIncome, ...prev]);
                    } else if (modalType === "expenses") {
                        const savedExpenseType = await addExpenseTypeService(payload);
                        setExpensesTypes(prev => [savedExpenseType, ...prev]);
                    }

                }

                closeModal();
            } catch (err) {
                console.error(`Error ${editingItem ? "updating" : "adding"} ${modalType}:`, err);
                showErrorToast(`Failed to ${editingItem ? "update" : "add"} ${modalType}`);
            }
        },
    });


    const handleAddNew = (type) => {
        if (!canEditSettings) return;
        if (
            (type === "coach" && !canUseCoachTypes) ||
            (type === "activity" && !canUseActivityTypes) ||
            (type === "member" && !canUseMemberTypes) ||
            (type === "branch" && !canUseBranches) ||
            (type === "income" && !canUseIncomeTypes) ||
            (type === "expenses" && !canUseExpenseTypes)
        ) {
            showErrorToast("This feature is not enabled for this gym");
            return;
        }
        setModalType(type);
        setEditingItem(null);
        formik.resetForm();
        setShowModal(true);
    };

    const handleEdit = (item, type) => {
        if (!canEditSettings) return;
        setModalType(type);
        setEditingItem(item);
        formik.setValues({ name: item.name });
        setShowModal(true);
    };

    const handleDelete = async (id, type) => {
        if (!canEditSettings) return;
        try {
            if (!window.confirm(`Are you sure you want to delete this ${type}?`)) {
                return;
            }

            if (type === "coach") {
                // await deleteCoachTypeService(id);
                setCoachTypes(coachTypes.filter(coach => getItemId(coach) !== id));
            } else if (type === "activity") {
                await deleteActivityType(id);
                setActivityTypes(activityTypes.filter(item => getItemId(item) !== id));
            } else if (type === "member") {
                await deleteMemberTypeService(id);
                setMemberTypes(memberTypes.filter(item => getItemId(item) !== id));
            } else if (type === "branch") {
                setBranchTypes(branchTypes.filter(branch => getItemId(branch) !== id));
            } else if (type === "income") {
                setIncomeTypes(incomeType.filter(income => getItemId(income) !== id));
            } else if (type === "expenses") {
                setExpensesTypes(expensesType.filter(expense => getItemId(expense) !== id));
            }
        } catch (err) {
            console.error(`Error deleting ${type}:`, err);
            showErrorToast(`Failed to delete ${type}`);
        }
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingItem(null);
        formik.resetForm();
    };

    const renderTypeList = (items, type, emptyText) => {
        if (loading) return <p className="text-sm text-gray-500">Loading...</p>;
        if (!items.length) return <p className="text-sm text-gray-500">{emptyText}</p>;

        return items.map((item) => (
            <div
                key={getItemId(item)}
                className="flex items-center justify-between p-4 border border-gray-100 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
                <span className="font-medium text-gray-900">{item.name || "-"}</span>
                {canEditSettings && (
                    <div className="flex space-x-2">
                        <button
                            onClick={() => handleEdit(item, type)}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                        >
                            <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => handleDelete(getItemId(item), type)}
                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors duration-200"
                        >
                            <Trash2 className="w-4 h-4" />
                        </button>
                    </div>
                )}
            </div>
        ));
    };

    return (
        <div className="min-h-screen bg-gray-50 p-6">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-2">Configuration Settings</h1>
                    <p className="text-gray-600">Manage gym configurations</p>
                </div>

                {error && (
                    <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {error}
                    </div>
                )}

                {/* Tab Navigation */}
                <div className="mb-8">
                    <div className="border-b border-gray-200">
                        <nav className="flex space-x-8">
                            {canUseCoachTypes && <button onClick={() => setActiveTab("coach")} className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === "coach" ? "border-blue-500 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}>
                                <div className="flex items-center space-x-2">
                                    <Award className="w-4 h-4" />
                                    <span>Coach</span>
                                </div>
                            </button>}
                            {canUseActivityTypes && <button onClick={() => setActiveTab("activity")} className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === "activity" ? "border-blue-500 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}>
                                <div className="flex items-center space-x-2">
                                    <Dumbbell className="w-4 h-4" />
                                    <span>Activity Types</span>
                                </div>
                            </button>}
                            {canUseMemberTypes && <button onClick={() => setActiveTab("member")} className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === "member" ? "border-blue-500 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}>
                                <div className="flex items-center space-x-2">
                                    <Users className="w-4 h-4" />
                                    <span>Member Types</span>
                                </div>
                            </button>}
                            {canUseBranches && <button onClick={() => setActiveTab("branches")} className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === "branches" ? "border-blue-500 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}>
                                <div className="flex items-center space-x-2">
                                    <Layers className="w-4 h-4" />
                                    <span>Branch</span>
                                </div>
                            </button>}
                            {canUseIncomeTypes && <button onClick={() => setActiveTab("income")} className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === "income" ? "border-blue-500 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}>
                                <div className="flex items-center space-x-2">
                                    <DollarSign className="w-4 h-4" />
                                    <span>Income</span>
                                </div>
                            </button>}
                            {canUseExpenseTypes && <button onClick={() => setActiveTab("expenses")} className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200 ${activeTab === "expenses" ? "border-blue-500 text-blue-600" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"}`}>
                                <div className="flex items-center space-x-2">
                                    <FileText className="w-4 h-4" />
                                    <span>Expenses</span>
                                </div>
                            </button>}
                        </nav>
                    </div>
                </div>

                {/* Coach Tab */}
                {activeTab === "coach" && canUseCoachTypes && (
                    <div className="space-y-6">
                        <div className="flex justify-between items-center">
                            <h2 className="text-xl font-semibold text-gray-900">Coach Configuration</h2>
                            {canEditSettings && (
                                <button
                                    onClick={() => handleAddNew("coach")}
                                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200"
                                >
                                    <Plus className="w-4 h-4" />
                                    <span>Add Coach</span>
                                </button>
                            )}
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                            <div className="p-6">
                                <div className="space-y-3">
                                    {renderTypeList(coachTypes, "coach", "No coach types found.")}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === "activity" && canUseActivityTypes && (
                    <div className="space-y-6">
                        <div className="flex justify-between items-center">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900">Activity Type Configuration</h2>
                                <p className="text-sm text-gray-500">Create reusable activities such as Taekwondo or BJJ.</p>
                            </div>
                            {canEditSettings && (
                                <button onClick={() => handleAddNew("activity")} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200">
                                    <Plus className="w-4 h-4" />
                                    <span>Add Activity Type</span>
                                </button>
                            )}
                        </div>
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                            <div className="p-6 space-y-3">
                                {renderTypeList(activityTypes, "activity", "No activity types found. Add one before configuring a package split.")}
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === "member" && canUseMemberTypes && (
                    <div className="space-y-6">
                        <div className="flex justify-between items-center">
                            <h2 className="text-xl font-semibold text-gray-900">Member Type Configuration</h2>
                            {canEditSettings && (
                                <button onClick={() => handleAddNew("member")} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200">
                                    <Plus className="w-4 h-4" />
                                    <span>Add Member Type</span>
                                </button>
                            )}
                        </div>
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                            <div className="p-6">
                                <div className="space-y-3">
                                    {renderTypeList(memberTypes, "member", "No member types found.")}
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {activeTab === "branches" && canUseBranches && (
                    <div className="space-y-6">
                        <div className="flex justify-between items-center">
                            <h2 className="text-xl font-semibold text-gray-900">Branch Configuration</h2>
                            {canEditSettings && <button onClick={() => handleAddNew("branch")} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200">
                                <Plus className="w-4 h-4" />
                                <span>Add Branch</span>
                            </button>}
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                            <div className="p-6">
                                <div className="space-y-3">
                                    {renderTypeList(branchTypes, "branch", "No branches found.")}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {activeTab === "income" && canUseIncomeTypes && (
                    <div className="space-y-6">
                        <div className="flex justify-between items-center">
                            <h2 className="text-xl font-semibold text-gray-900">Income Configuration</h2>
                            {canEditSettings && <button onClick={() => handleAddNew("income")} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200">
                                <Plus className="w-4 h-4" />
                                <span>Add Income</span>
                            </button>}
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                            <div className="p-6">
                                <div className="space-y-3">
                                    {renderTypeList(incomeType, "income", "No income types found.")}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {activeTab === "expenses" && canUseExpenseTypes && (
                    <div className="space-y-6">
                        <div className="flex justify-between items-center">
                            <h2 className="text-xl font-semibold text-gray-900">Expenses Configuration</h2>
                            {canEditSettings && <button onClick={() => handleAddNew("expenses")} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors duration-200">
                                <Plus className="w-4 h-4" />
                                <span>Add Expenses</span>
                            </button>}
                        </div>

                        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
                            <div className="p-6">
                                <div className="space-y-3">
                                    {renderTypeList(expensesType, "expenses", "No expense types found.")}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {/* AddDialog Modal */}
                <AddDialog
                    title={`${editingItem ? 'Edit' : 'Add New'} ${modalType === "coach" ? "Coach" :
                            modalType === "activity" ? "Activity Type" :
                            modalType === "member" ? "Member Type" :
                            modalType === "branch" ? "Branch" :
                                modalType === "income" ? "Income" : "Expenses"}`}
                    isOpen={showModal}
                    onClose={closeModal}
                >
                    <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
                        <TextInput
                            label={`${modalType === "coach" ? "Coach" :
                                    modalType === "activity" ? "Activity" :
                                    modalType === "member" ? "Member" :
                                    modalType === "branch" ? "Branch" :
                                        modalType === "income" ? "Income" : "Expenses"} Type Name`}
                            type="text"
                            name="name"
                            value={formik.values.name}
                            onChange={formik.handleChange}
                            placeholder={`Enter ${modalType === "coach" ? "coach" :
                                    modalType === "activity" ? "activity" :
                                    modalType === "member" ? "member" :
                                    modalType === "branch" ? "branch" :
                                        modalType === "income" ? "income" : "expenses"} type name`}
                        />
                        {formik.touched.name && formik.errors.name ? (
                            <p className="text-red-500 text-sm">{formik.errors.name}</p>
                        ) : null}

                        <button
                            type="submit"
                            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 transition-colors duration-200"
                        >
                            {editingItem ? 'Update' : 'Add'} {modalType === "coach" ? "Coach" :
                                    modalType === "activity" ? "Activity" :
                                    modalType === "member" ? "Member" :
                                    modalType === "branch" ? "Branch" :
                                        modalType === "income" ? "Income" : "Expenses"} Type
                        </button>
                    </form>
                </AddDialog>

            </div>
        </div>
    );
};

export default Settings;
